// Print Helper for Dinexa POS
// Supports System Print (via Iframe) with Marathi/Hindi font rendering and raw Web Bluetooth ESC/POS printing.
import { POS_MENU } from '../data/mockData';

// 1. System Print: Renders a DOM element inside a hidden iframe styled for 58mm/80mm thermal receipt sizes.
export const printElement = (elementId, paperSize = '58mm') => {
    const printContent = document.getElementById(elementId);
    if (!printContent) {
        console.error(`Element #${elementId} not found for printing.`);
        return false;
    }

    const rawFeed = localStorage.getItem('dinexa_print_feed_lines');
    const userStr = sessionStorage.getItem('react_pos_current_user') || localStorage.getItem('react_pos_current_user');
    let feedLines = 3;
    try {
        if (rawFeed !== null && rawFeed !== undefined && !isNaN(parseInt(rawFeed, 10))) {
            feedLines = parseInt(rawFeed, 10);
        } else if (userStr) {
            const u = JSON.parse(userStr);
            if (u.print_feed_lines !== undefined && u.print_feed_lines !== null) {
                feedLines = parseInt(u.print_feed_lines, 10);
            }
        }
    } catch (e) { }
    const feedHeightPx = Math.max(0, feedLines) * 18;

    // Find or create hidden iframe
    let iframe = document.getElementById('dinexa-print-iframe');
    if (!iframe) {
        iframe = document.createElement('iframe');
        iframe.id = 'dinexa-print-iframe';
        iframe.style.position = 'absolute';
        iframe.style.width = '0px';
        iframe.style.height = '0px';
        iframe.style.border = 'none';
        iframe.style.left = '-9999px';
        document.body.appendChild(iframe);
    }

    const pageSize = paperSize || localStorage.getItem('dinexa_printer_page_size') || '58mm';
    const is80 = pageSize === '80mm';
    const sizeStr = is80 ? '70mm' : '46mm';
    const pageStr = is80 ? '72mm auto' : '46mm auto';

    const doc = iframe.contentWindow.document;
    doc.open();
    doc.write(`
        <html>
            <head>
                <title>Dinexa Thermal Print</title>
                <style>
                    @import url('https://fonts.googleapis.com/css2?family=Noto+Sans+Devanagari:wght@400;700;900&display=swap');
                    
                    @page {
                        size: ${pageStr};
                        margin: 0 !important;
                    }
                    
                    body, html {
                        margin: 0 !important;
                        padding: 0 !important;
                        width: ${sizeStr} !important;
                        max-width: ${sizeStr} !important;
                        box-sizing: border-box !important;
                        font-family: 'Noto Sans Devanagari', 'Courier New', Courier, monospace;
                        color: #000000;
                        background-color: #ffffff;
                        overflow: hidden !important;
                        -webkit-print-color-adjust: exact;
                        print-color-adjust: exact;
                    }

                    img {
                        width: 100% !important;
                        max-width: 100% !important;
                        height: auto !important;
                        display: block !important;
                        margin: 0 !important;
                        padding: 0 !important;
                    }

                    /* Thermal formatting utilities */
                    .text-center { text-align: center; }
                    .text-right { text-align: right; }
                    .bold { font-weight: bold; }
                    .header-title { font-size: 14px; font-weight: 900; line-height: 1.2; }
                    .bold-title { font-size: 15px; font-weight: 900; }
                    .dashed-line { border-bottom: 1px dashed #000000; margin: 6px 0; }
                    .double-dashed-line { border-bottom: 2px dashed #000000; margin: 8px 0; }
                    
                    /* Custom styles for tables/lists */
                    table {
                        width: 100%;
                        border-collapse: collapse;
                        font-size: 11px;
                    }
                    th, td {
                        padding: 2px 0;
                        font-family: 'Noto Sans Devanagari', 'Courier New', Courier, monospace;
                    }
                    
                    /* Barcode simulation */
                    .barcode-box {
                        display: flex;
                        flex-direction: column;
                        align-items: center;
                        margin: 8px 0;
                    }
                    .barcode-bars {
                        height: 24px;
                        width: 120px;
                        background: #000000;
                        background-image: repeating-linear-gradient(90deg, transparent, transparent 2px, #ffffff 2px, #ffffff 4px, transparent 4px, transparent 8px);
                    }
                </style>
            </head>
            <body>
                <div class="print-wrapper">
                    ${printContent.innerHTML}
                </div>
                ${feedHeightPx > 0 ? `
                    <div style="height: ${feedHeightPx}px; width: 100%;"></div>
                ` : ''}
                <script>
                    window.onload = function() {
                        setTimeout(function() {
                            window.focus();
                            window.print();
                        }, 250);
                    };
                </script>
            </body>
        </html>
    `);
    doc.close();
    return true;
};

// Print an element specifically for A4 reports
export const printA4Report = (elementId) => {
    const printContent = document.getElementById(elementId);
    if (!printContent) {
        console.error(`Element #${elementId} not found for A4 printing.`);
        return false;
    }

    let iframe = document.getElementById('dinexa-a4-print-iframe');
    if (!iframe) {
        iframe = document.createElement('iframe');
        iframe.id = 'dinexa-a4-print-iframe';
        iframe.style.position = 'absolute';
        iframe.style.width = '0px';
        iframe.style.height = '0px';
        iframe.style.border = 'none';
        iframe.style.left = '-9999px';
        document.body.appendChild(iframe);
    }

    const doc = iframe.contentWindow.document;
    doc.open();
    doc.write(`
        <html>
            <head>
                <title>Account Statement</title>
                <style>
                    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700;800;900&display=swap');
                    @page {
                        size: A4;
                        margin: 15mm;
                    }
                    body {
                        font-family: 'Inter', sans-serif;
                        color: #0f172a;
                        margin: 0;
                        padding: 0;
                        -webkit-print-color-adjust: exact;
                        print-color-adjust: exact;
                    }
                    .report-container {
                        width: 100%;
                    }
                    table {
                        width: 100%;
                        border-collapse: collapse;
                        margin-bottom: 20px;
                        font-size: 12px;
                    }
                    th, td {
                        border: 1px solid #cbd5e1;
                        padding: 8px 12px;
                        text-align: left;
                    }
                    th {
                        background-color: #f1f5f9 !important;
                        font-weight: 700;
                        color: #334155;
                    }
                    .text-right { text-align: right; }
                    .text-center { text-align: center; }
                    .font-bold { font-weight: 700; }
                    .total-row th, .total-row td {
                        background-color: #f8fafc !important;
                        font-weight: 800;
                    }
                    .grand-total-row th, .grand-total-row td {
                        background-color: #e2e8f0 !important;
                        font-weight: 900;
                        font-size: 14px;
                    }
                    .footer {
                        position: fixed;
                        bottom: 0;
                        left: 0;
                        width: 100%;
                        display: flex;
                        justify-content: space-between;
                        font-size: 10px;
                        color: #64748b;
                        padding-top: 5px;
                        border-top: 1px solid #e2e8f0;
                    }
                    /* For repeating table headers on every page */
                    thead { display: table-header-group; }
                    tfoot { display: table-footer-group; }
                </style>
            </head>
            <body>
                <div class="report-container">
                    ${printContent.innerHTML}
                </div>
                <div class="footer">
                    <span>Generated by DinexaPOS</span>
                    <span>Page Number varies by printer</span>
                </div>
                <script>
                    window.onload = function() {
                        setTimeout(function() {
                            window.focus();
                            window.print();
                        }, 500);
                    };
                </script>
            </body>
        </html>
    `);
    doc.close();
    return true;
};

// 2. Direct Web Bluetooth Print (Sends ESC/POS commands)
export class BluetoothPrinterManager {
    static device = null;
    static characteristic = null;
    static serialPort = null;
    static usbDevice = null;

    // Get persisted printer settings
    static getSettings() {
        const pageSize = localStorage.getItem('dinexa_printer_page_size') || '58mm';
        const fontSize = parseInt(localStorage.getItem('dinexa_printer_font_size') || '14', 10);
        const fontScale = (fontSize >= 10 && fontSize <= 20) ? fontSize / 14 : 1.0;
        const density = localStorage.getItem('dinexa_printer_density') || 'dark';

        let receiptTemplate = 'coupon1';
        try {
            const raw1 = localStorage.getItem('react_pos_receipt_template');
            const raw2 = localStorage.getItem('dinexa_receipt_template');
            const userStr = sessionStorage.getItem('react_pos_current_user') || localStorage.getItem('react_pos_current_user');
            const currentUser = userStr ? JSON.parse(userStr) : null;

            if (raw1) {
                receiptTemplate = (raw1.startsWith('"') && raw1.endsWith('"')) ? JSON.parse(raw1) : raw1;
            } else if (raw2) {
                receiptTemplate = (raw2.startsWith('"') && raw2.endsWith('"')) ? JSON.parse(raw2) : raw2;
            } else if (currentUser && currentUser.receipt_template) {
                receiptTemplate = currentUser.receipt_template;
            }
        } catch (e) {
            receiptTemplate = 'coupon1';
        }

        let threshold = 205;
        if (density === 'normal') threshold = 180;
        if (density === 'extra-dark') threshold = 225;

        let printFeedLines = 3;
        try {
            const rawFeed = localStorage.getItem('dinexa_print_feed_lines');
            const userStr = sessionStorage.getItem('react_pos_current_user') || localStorage.getItem('react_pos_current_user');
            const currentUser = userStr ? JSON.parse(userStr) : null;
            if (rawFeed !== null && rawFeed !== undefined && !isNaN(parseInt(rawFeed, 10))) {
                printFeedLines = parseInt(rawFeed, 10);
            } else if (currentUser && currentUser.print_feed_lines !== undefined && currentUser.print_feed_lines !== null) {
                printFeedLines = parseInt(currentUser.print_feed_lines, 10);
            }
        } catch (e) {
            printFeedLines = 3;
        }

        let marginOffset = 0;
        try {
            const rawMargin = localStorage.getItem('dinexa_printer_margin_offset');
            if (rawMargin !== null && rawMargin !== undefined && !isNaN(parseInt(rawMargin, 10))) {
                marginOffset = Math.max(0, parseInt(rawMargin, 10));
            }
        } catch (e) {
            marginOffset = 0;
        }

        let printableWidth = 300;
        try {
            const rawWidth = localStorage.getItem('dinexa_printer_content_width');
            if (rawWidth !== null && rawWidth !== undefined && !isNaN(parseInt(rawWidth, 10))) {
                printableWidth = Math.max(240, Math.min(360, parseInt(rawWidth, 10)));
            }
        } catch (e) {
            printableWidth = 300;
        }

        return {
            pageSize,
            fontSize,
            fontScale,
            density,
            threshold,
            receiptTemplate,
            printFeedLines,
            marginOffset,
            printableWidth
        };
    }

    // Discover characteristics from server
    static async discoverCharacteristics(server) {
        const uuids = [
            '000018f0-0000-1000-8000-00805f9b34fb',
            '00001101-0000-1000-8000-00805f9b34fb',
            '000018f1-0000-1000-8000-00805f9b34fb',
            '0000e808-0000-1000-8000-00805f9b34fb',
            '0000ae30-0000-1000-8000-00805f9b34fb'
        ];

        let service = null;
        for (const uuid of uuids) {
            try {
                service = await server.getPrimaryService(uuid);
                if (service) break;
            } catch (e) {
                console.log(`Service ${uuid} not found on this printer, trying next...`);
            }
        }

        if (!service) {
            try {
                const services = await server.getPrimaryServices();
                if (services.length > 0) {
                    service = services[0];
                }
            } catch (e) {
                console.error("Could not fetch primary services:", e);
            }
        }

        if (!service) {
            throw new Error("No compatible GATT primary service found on the printer.");
        }

        const characteristics = await service.getCharacteristics();
        const characteristic = characteristics.find(c =>
            c.properties.write || c.properties.writeWithoutResponse
        );

        if (!characteristic) {
            throw new Error("Printer Write characteristic not found.");
        }

        return characteristic;
    }

    // Connect to Bluetooth Printer
    static async connect() {
        if (this.characteristic) {
            return this.device ? this.device.name : "Connected Printer";
        }

        // Try auto-reconnect using previously authorized browser list
        if (navigator.bluetooth && navigator.bluetooth.getDevices) {
            try {
                const devices = await navigator.bluetooth.getDevices();
                const savedName = localStorage.getItem('dinexa_printer_device_name');
                const matchedDevice = devices.find(d => d.name === savedName) || devices[0];
                if (matchedDevice) {
                    this.device = matchedDevice;
                    this.device.addEventListener('gattserverdisconnected', () => {
                        this.characteristic = null;
                        this.device = null;
                    });
                    const server = await this.device.gatt.connect();
                    this.characteristic = await this.discoverCharacteristics(server);
                    return this.device.name || "Bluetooth Printer";
                }
            } catch (e) {
                console.warn("Auto-reconnect to paired bluetooth device failed, showing selector:", e);
            }
        }

        try {
            const uuids = [
                '000018f0-0000-1000-8000-00805f9b34fb',
                '00001101-0000-1000-8000-00805f9b34fb',
                '000018f1-0000-1000-8000-00805f9b34fb',
                '0000e808-0000-1000-8000-00805f9b34fb',
                '0000ae30-0000-1000-8000-00805f9b34fb',
                '0000ffe0-0000-1000-8000-00805f9b34fb',
                'e7810a71-73ae-499d-8c15-faa9aef0c3f2',
                '49535343-fe7d-4ae5-8fa9-9fafd205e455',
                '0000ff00-0000-1000-8000-00805f9b34fb',
                '0000ffff-0000-1000-8000-00805f9b34fb'
            ];

            this.device = await navigator.bluetooth.requestDevice({
                acceptAllDevices: true,
                optionalServices: uuids
            });

            this.device.addEventListener('gattserverdisconnected', () => {
                this.characteristic = null;
                this.device = null;
            });

            const server = await this.device.gatt.connect();
            this.characteristic = await this.discoverCharacteristics(server);

            localStorage.setItem('dinexa_printer_device_name', this.device.name || '');
            return this.device.name || "Bluetooth Printer";
        } catch (error) {
            console.error("Bluetooth printer connection failed:", error);
            throw error;
        }
    }

    // English, Marathi, Hindi translations database for restaurant receipt printing
    static TRANSLATIONS = {
        mr: {
            companyName: "स्वाद महाराष्ट्रीयन ढाबा",
            address: "NH-४ हायवे, कोल्हापूर बायपास",
            tel: "Tel: +९१ ९८७६५४३२१०",
            date: "दिनांक",
            time: "वेळ",
            token: "टोकन",
            type: "प्रकार",
            payment: "पेमेंट",
            item: "पदार्थ",
            qty: "नग",
            amt: "किंमत",
            subtotal: "एकूण मुळ किंमत",
            gst: "GST (५%)",
            discount: "सूट / डिस्काउंट",
            totalPaid: "एकूण बिल रक्कम",
            thankYou: "धन्यवाद! पुन्हा भेटूया!",
            kotHeader: "किचन ऑर्डर तिकीट (KOT)",
            table: "टेबल",
            waiter: "वेटर्स",
            kotNo: "KOT नं",
            instruction: "सूचना",
            poweredBy: "Dinexa Smart POS Kitchen Hub",
            // Collection Print translations
            collectionHeader: "दुध संकलन पावती",
            customerName: "ग्राहकाचे नाव",
            collectionDate: "संकलन तारीख",
            collectionTime: "संकलन वेळ",
            litres: "लिटर",
            fat: "फॅट",
            snf: "SNF",
            rate: "दर",
            amount: "एकूण रक्कम"
        },
        hi: {
            companyName: "स्वाद महाराष्ट्रीयन ढाबा",
            address: "NH-4 हाईवे, कोल्हापुर बाईपास",
            tel: "Tel: +91 9876543210",
            date: "दिनांक",
            time: "समय",
            token: "टोकन",
            type: "प्रकार",
            payment: "भुगतान",
            item: "सामग्री",
            qty: "मात्रा",
            amt: "मूल्य",
            subtotal: "कुल मूल्य",
            gst: "GST (5%)",
            discount: "छूट",
            totalPaid: "कुल भुगतान",
            thankYou: "धन्यवाद! फिर मिलेंगे!",
            kotHeader: "रसोई ऑर्डर टिकट (KOT)",
            table: "टेबल",
            waiter: "वेंडर / वेटर",
            kotNo: "KOT संख्या",
            instruction: "निर्देश",
            poweredBy: "Dinexa Smart POS Kitchen Hub",
            // Collection Print translations
            collectionHeader: "संग्रह रसीद",
            customerName: "ग्राहक का नाम",
            collectionDate: "संग्रह तारीख",
            collectionTime: "संग्रह समय",
            litres: "लीटर",
            fat: "फैट",
            snf: "SNF",
            rate: "दर",
            amount: "कुल राशि"
        },
        en: {
            companyName: "SWAD MAHARASHTRIAN DHABA",
            address: "NH-4 Highway, Kolhapur Bypass",
            tel: "Tel: +91 9876543210",
            date: "Date",
            time: "Time",
            token: "TOKEN",
            type: "Type",
            payment: "Payment",
            item: "Item",
            qty: "Qty",
            amt: "Amt",
            subtotal: "Subtotal",
            gst: "GST (5%)",
            discount: "Discount",
            totalPaid: "TOTAL PAID",
            thankYou: "THANK YOU! VISIT AGAIN",
            kotHeader: "KITCHEN ORDER TICKET",
            table: "TABLE",
            waiter: "Waiter",
            kotNo: "KOT No",
            instruction: "Instruction",
            poweredBy: "Dinexa Smart POS Kitchen Hub",
            // Collection Print translations
            collectionHeader: "Collection Receipt",
            customerName: "Customer Name",
            collectionDate: "Collection Date",
            collectionTime: "Collection Time",
            litres: "Litres",
            fat: "Fat",
            snf: "SNF",
            rate: "Rate",
            amount: "Total Amount"
        }
    };

    // Print Receipt via Raw ESC/POS commands (Standard English fallback/numbers)
    static async printReceipt(order) {
        if (!this.characteristic) {
            throw new Error("No printer connected. Pair a Bluetooth printer first.");
        }

        const ESC = 0x1b;
        const GS = 0x1d;
        const encode = (str) => new TextEncoder().encode(str);

        // Command byte streams
        const init = new Uint8Array([ESC, 0x40]);
        const center = new Uint8Array([ESC, 0x61, 1]);
        const left = new Uint8Array([ESC, 0x61, 0]);
        const right = new Uint8Array([ESC, 0x61, 2]);
        const boldOn = new Uint8Array([ESC, 0x45, 1]);
        const boldOff = new Uint8Array([ESC, 0x45, 0]);
        const doubleSize = new Uint8Array([GS, 0x21, 0x11]);
        const normalSize = new Uint8Array([GS, 0x21, 0x00]);
        const feedAndCut = new Uint8Array([0x0a, 0x0a, 0x0a, 0x0a, ESC, 0x69]); // paper cut

        let data = [];
        const add = (bytes) => data.push(...bytes);
        const addText = (text) => data.push(...encode(text));

        // 1. Header
        add(init);
        add(center);
        add(boldOn);
        add(doubleSize);
        addText("SWAD MAHARASHTRA\n");
        add(normalSize);
        addText("NH-4 Highway, Kolhapur\n");
        addText("Tel: +91 9876543210\n");
        add(boldOff);
        addText("--------------------------------\n");

        // 2. Metadata
        add(left);
        addText(`Order: ${order.id}\n`);
        addText(`Date: ${new Date(order.timestamp).toLocaleDateString()}\n`);
        addText(`Time: ${new Date(order.timestamp).toLocaleTimeString()}\n`);
        addText(`Type: ${order.type}\n`);
        addText("--------------------------------\n");

        // 3. Items list
        addText("Item            Qty   Price\n");
        addText("--------------------------------\n");
        order.items.forEach(item => {
            // Format item name to fit 16 chars
            const name = item.name.substring(0, 15).padEnd(16, ' ');
            const qty = item.quantity.toString().padStart(4, ' ');
            const price = `Rs.${item.price * item.quantity}`.padStart(10, ' ');
            addText(`${name}${qty}${price}\n`);
            if (item.remarks) {
                addText(` * Note: ${item.remarks}\n`);
            }
        });
        addText("--------------------------------\n");

        // 4. Financials
        add(right);
        addText(`Subtotal: Rs.${order.subtotal.toFixed(0)}\n`);
        addText(`GST (5%): Rs.${order.tax.toFixed(0)}\n`);
        if (order.discount > 0) {
            addText(`Discount: -Rs.${order.discount.toFixed(0)}\n`);
        }
        add(boldOn);
        addText(`TOTAL PAID: Rs.${order.total.toFixed(0)}\n`);
        add(boldOff);
        addText("--------------------------------\n");

        // 5. Footer
        add(center);
        addText("THANK YOU! VISIT AGAIN\n");
        addText("Powered by Dinexa POS\n");
        add(feedAndCut);

        // Send data in chunks
        const bytes = new Uint8Array(data);
        const chunkSize = 256;
        const writeMethod = this.characteristic.writeValueWithoutResponse ?
            (chunk) => this.characteristic.writeValueWithoutResponse(chunk) :
            (chunk) => this.characteristic.writeValue(chunk);

        for (let i = 0; i < bytes.length; i += chunkSize) {
            const chunk = bytes.slice(i, i + chunkSize);
            await writeMethod(chunk);
            await new Promise(resolve => setTimeout(resolve, 10));
        }
    }

    // Helper to send binary chunks to physical printer with high-performance micro-pacing
    static async sendRawData(binaryData, options = {}) {
        const connectionType = localStorage.getItem('dinexa_printer_connection_type') || 'system';

        if (connectionType === 'bluetooth') {
            if (!this.characteristic) {
                await this.connect();
            }
            if (!this.characteristic) {
                throw new Error("No Bluetooth printer connected. Please connect from printer setup.");
            }

            const chunkSize = options.chunkSize || 128;
            const microDelay = options.delay !== undefined ? options.delay : 3;

            const writeMethod = this.characteristic.writeValueWithoutResponse ?
                (chunk) => this.characteristic.writeValueWithoutResponse(chunk) :
                (chunk) => this.characteristic.writeValue(chunk);

            for (let i = 0; i < binaryData.length; i += chunkSize) {
                const chunk = binaryData.slice(i, i + chunkSize);
                await writeMethod(chunk);
                if (microDelay > 0) {
                    await new Promise(resolve => setTimeout(resolve, microDelay));
                }
            }
        } else if (connectionType === 'serial') {
            if (!('serial' in navigator)) {
                throw new Error("Web Serial API is not supported in this browser. Please use Chrome/Edge.");
            }
            if (!this.serialPort) {
                const ports = await navigator.serial.getPorts();
                if (ports.length > 0) {
                    this.serialPort = ports[0];
                } else {
                    throw new Error("No paired Serial COM Port found. Please pair it in Printer Setup first.");
                }
            }

            try {
                if (!this.serialPort.readable) {
                    await this.serialPort.open({ baudRate: 9600 });
                }

                const writer = this.serialPort.writable.getWriter();
                const chunkSize = options.chunkSize || 128;
                const microDelay = options.delay !== undefined ? options.delay : 4;
                for (let i = 0; i < binaryData.length; i += chunkSize) {
                    const chunk = binaryData.slice(i, i + chunkSize);
                    await writer.write(chunk);
                    if (microDelay > 0) {
                        await new Promise(resolve => setTimeout(resolve, microDelay));
                    }
                }
                writer.releaseLock();
            } finally {
                if (this.serialPort) {
                    try {
                        await this.serialPort.close();
                    } catch (e) { }
                }
            }
        } else if (connectionType === 'usb') {
            if (!('usb' in navigator)) {
                throw new Error("WebUSB API is not supported in this browser. Please use Chrome/Edge.");
            }
            if (!this.usbDevice) {
                const devices = await navigator.usb.getDevices();
                if (devices.length > 0) {
                    this.usbDevice = devices[0];
                } else {
                    throw new Error("No paired USB printer found. Please pair it in Printer Setup first.");
                }
            }

            if (!this.usbDevice.opened) {
                await this.usbDevice.open();
                await this.usbDevice.selectConfiguration(1);
                await this.usbDevice.claimInterface(0);
            }

            let endpoint = null;
            const endpoints = this.usbDevice.configuration.interfaces[0].alternates[0].endpoints;
            for (const ep of endpoints) {
                if (ep.direction === 'out') {
                    endpoint = ep;
                    break;
                }
            }

            if (!endpoint) {
                throw new Error("Bulk Out endpoint not found on USB device.");
            }

            const chunkSize = options.chunkSize || 512;
            for (let i = 0; i < binaryData.length; i += chunkSize) {
                const chunk = binaryData.slice(i, i + chunkSize);
                await this.usbDevice.transferOut(endpoint.endpointNumber, chunk);
                await new Promise(resolve => setTimeout(resolve, 2));
            }
        } else {
            throw new Error(`Connection type ${connectionType} does not support direct raw writing.`);
        }
    }

    // High-Performance Smooth Streaming Canvas Printer
    static async printCanvas(canvas) {
        const settings = this.getSettings();
        const threshold = settings.threshold;

        const ctx = canvas.getContext('2d');
        const W = canvas.width;
        const H = canvas.height;
        const widthBytes = W / 8;

        const ESC = 0x1B;
        const GS = 0x1D;

        // Build entire ESC/POS byte sequence
        const allCmds = [];

        // 1. Set Line Spacing 0 (ESC 3 0) to ensure zero inter-line gaps
        allCmds.push(ESC, 0x33, 0x00);

        // 2. Stream sliced graphics smoothly in bands of 96 pixels
        const sliceHeight = 96;
        for (let yStart = 0; yStart < H; yStart += sliceHeight) {
            const currentSliceHeight = Math.min(sliceHeight, H - yStart);
            const imgData = ctx.getImageData(0, yStart, W, currentSliceHeight);
            const pixels = imgData.data;
            const sliceBitmap = [];

            for (let y = 0; y < currentSliceHeight; y++) {
                for (let xByte = 0; xByte < widthBytes; xByte++) {
                    let byteVal = 0;
                    for (let bit = 0; bit < 8; bit++) {
                        const x = xByte * 8 + bit;
                        const idx = (y * W + x) * 4;
                        const r = pixels[idx];
                        const g = pixels[idx + 1];
                        const b = pixels[idx + 2];
                        const a = pixels[idx + 3];

                        let isBlack = 0;
                        if (a > 50) {
                            const brightness = (r + g + b) / 3;
                            if (brightness < threshold) {
                                isBlack = 1;
                            }
                        }
                        if (isBlack) {
                            byteVal |= (1 << (7 - bit));
                        }
                    }
                    sliceBitmap.push(byteVal);
                }
            }

            allCmds.push(GS, 0x76, 0x30, 0x00);
            allCmds.push(widthBytes % 256, Math.floor(widthBytes / 256));
            allCmds.push(currentSliceHeight % 256, Math.floor(currentSliceHeight / 256));
            allCmds.push(...sliceBitmap);
        }

        // 3. Reset line spacing & Feed paper cleanly strictly based on printFeedLines setting
        const feedCount = typeof settings.printFeedLines === 'number' ? Math.max(0, Math.min(15, settings.printFeedLines)) : 3;
        allCmds.push(ESC, 0x32);
        if (feedCount > 0) {
            for (let i = 0; i < feedCount; i++) {
                allCmds.push(0x0A);
            }
        }

        const fullBinaryData = new Uint8Array(allCmds);
        // Send full binary data in ONE unified call (opens USB/Bluetooth/Serial session ONCE)
        await this.sendRawData(fullBinaryData, { chunkSize: 128, delay: 3 });
    }

    // Generates the canvas element for the receipt (used for both preview and printing)
    static async generateReceiptCanvas(order, lang = 'mr', paperSize) {
        const settings = BluetoothPrinterManager.getSettings();
        const actualPaperSize = paperSize || settings.pageSize;
        const is80mm = actualPaperSize === '80mm';
        const width = is80mm ? 576 : 384; // Standard full printable resolution for 58mm (384px) & 80mm (576px)

        const scale = settings.fontScale || 1.0;

        // Use a temporary scratch canvas of high height to draw the items dynamically
        const tempCanvas = document.createElement('canvas');
        tempCanvas.width = width;
        tempCanvas.height = 3000; // tall enough for any receipt

        const ctx = tempCanvas.getContext('2d');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, tempCanvas.width, tempCanvas.height);
        ctx.fillStyle = '#000000';

        const trans = BluetoothPrinterManager.TRANSLATIONS[lang] || BluetoothPrinterManager.TRANSLATIONS.en;
        let y = Math.round(24 * scale);

        const template = settings.receiptTemplate || 'coupon1';
        const paymentMode = order.paymentMethod || 'cash';

        if (template === 'coupon1') {
            y = await this._renderCoupon1Template(ctx, order, trans, width, scale, y, lang, paymentMode);
        } else if (template === 'coupon2') {
            y = await this._renderCoupon2Template(ctx, order, trans, width, scale, y, lang, paymentMode);
        } else if (template === 'format2') {
            y = await this._renderTemplate2(ctx, order, trans, width, scale, y, lang, paymentMode);
        } else {
            y = await this._renderTemplate1(ctx, order, trans, width, scale, y, lang, paymentMode);
        }

        const finalHeight = y;
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = finalHeight;
        const finalCtx = canvas.getContext('2d');
        finalCtx.drawImage(tempCanvas, 0, 0, width, finalHeight, 0, 0, width, finalHeight);

        return canvas;
    }

    // Multilingual Canvas printer for Counter bill
    static async printReceiptCanvas(order, lang = 'mr', paperSize) {
        const canvas = await this.generateReceiptCanvas(order, lang, paperSize);
        await this.printCanvas(canvas);
    }

    // Generate a Base64 Image URL for live UI preview
    static async generateReceiptDataURL(order, lang = 'mr', paperSize) {
        const canvas = await this.generateReceiptCanvas(order, lang, paperSize);
        return canvas.toDataURL('image/png');
    }

    // ---------- Collection Print Methods ----------

    /**
     * Print a collection receipt (e.g., milk collection) with the specified language.
     * @param {Object} collectionData - { customerName, date, time, litres, fat, snf, rate, amount, collectionId? }
     * @param {string} lang - 'mr', 'hi', 'en'
     * @param {string} paperSize - '58mm' or '80mm'
     */
    static async printCollection(collectionData, lang = 'mr', paperSize) {
        const canvas = await this.generateCollectionCanvas(collectionData, lang, paperSize);
        await this.printCanvas(canvas);
    }

    /**
     * Generate a canvas for the collection receipt.
     */
    static async generateCollectionCanvas(collectionData, lang = 'mr', paperSize) {
        const settings = BluetoothPrinterManager.getSettings();
        const actualPaperSize = paperSize || settings.pageSize;
        const is80mm = actualPaperSize === '80mm';
        const width = is80mm ? 576 : 384;
        const scale = settings.fontScale || 1.0;

        const tempCanvas = document.createElement('canvas');
        tempCanvas.width = width;
        tempCanvas.height = 1500;

        const ctx = tempCanvas.getContext('2d');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, tempCanvas.width, tempCanvas.height);
        ctx.fillStyle = '#000000';

        const trans = BluetoothPrinterManager.TRANSLATIONS[lang] || BluetoothPrinterManager.TRANSLATIONS.en;
        let y = Math.round(24 * scale);

        // Draw the collection template
        y = await this._renderCollectionTemplate(ctx, collectionData, trans, width, scale, y);

        const finalHeight = y;
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = finalHeight;
        const finalCtx = canvas.getContext('2d');
        finalCtx.drawImage(tempCanvas, 0, 0, width, finalHeight, 0, 0, width, finalHeight);
        return canvas;
    }

    // Private rendering method for Collection Print
    static async _renderCollectionTemplate(ctx, data, trans, width, scale, y) {
        const is80mm = width > 450;
        const settings = BluetoothPrinterManager.getSettings();
        const marginOffset = settings.marginOffset !== undefined ? settings.marginOffset : 0;
        const contentWidth = is80mm ? 536 : (settings.printableWidth || 300);
        const L_MARGIN = is80mm ? 16 : marginOffset;
        const R_BOUND = is80mm ? width - 20 : Math.min(360, L_MARGIN + contentWidth);
        const C_X = (L_MARGIN + R_BOUND) / 2;

        const drawTextCenter = (text, fontSizePx, fontStyle = '') => {
            ctx.font = `${fontStyle} ${fontSizePx}px Arial, sans-serif`;
            ctx.textAlign = 'center';
            ctx.fillText(text, width / 2, y);
        };

        const drawTextRow = (leftText, rightText, fontSizePx, fontStyle = '', leftOffset = L_MARGIN, rightOffset = width - R_BOUND) => {
            ctx.font = `${fontStyle} ${fontSizePx}px Arial, sans-serif`;
            ctx.textAlign = 'left';
            ctx.fillText(leftText, leftOffset, y);
            ctx.textAlign = 'right';
            ctx.fillText(rightText, width - rightOffset, y);
        };

        const drawTextCenterMultiline = (text, fontSizePx, fontStyle = '', lineHeightMultiplier = 1.2) => {
            ctx.font = `${fontStyle} ${fontSizePx}px Arial, sans-serif`;
            ctx.textAlign = 'center';
            const words = text.split(' ');
            let line = '';
            const lineHeight = Math.round(fontSizePx * lineHeightMultiplier);
            const maxWidth = R_BOUND - L_MARGIN;
            for (let n = 0; n < words.length; n++) {
                const testLine = line + words[n] + ' ';
                const metrics = ctx.measureText(testLine);
                if (metrics.width > maxWidth && n > 0) {
                    ctx.fillText(line.trim(), width / 2, y);
                    line = words[n] + ' ';
                    y += lineHeight;
                } else {
                    line = testLine;
                }
            }
            ctx.fillText(line.trim(), width / 2, y);
            y += Math.round(fontSizePx * 1.2);
        };

        const drawSeparator = () => {
            ctx.beginPath();
            ctx.setLineDash([4, 4]);
            ctx.moveTo(L_MARGIN, y - Math.round(6 * scale));
            ctx.lineTo(R_BOUND, y - Math.round(6 * scale));
            ctx.lineWidth = 1;
            ctx.stroke();
            ctx.setLineDash([]);
        };

        // 1. Company Name
        const currentUser = JSON.parse(sessionStorage.getItem('react_pos_current_user') || localStorage.getItem('react_pos_current_user') || '{}');
        const hotelName = (currentUser.is_multi_branch && currentUser.branch_name && currentUser.branch_name.toLowerCase() !== 'main branch' ? currentUser.branch_name : currentUser.tenant_name) || currentUser.name || trans.companyName;
        drawTextCenterMultiline(hotelName.toUpperCase(), Math.round(18 * scale), '900');

        // 2. Collection Header
        drawTextCenter(trans.collectionHeader, Math.round(15 * scale), 'bold');
        y += Math.round(22 * scale);

        // 3. Customer Name (if provided)
        if (data.customerName) {
            drawTextCenter(data.customerName, Math.round(13 * scale));
            y += Math.round(18 * scale);
        }

        // 4. Date & Time (two columns)
        const dateStr = data.date || new Date().toLocaleDateString();
        const timeStr = data.time || new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        drawTextRow(`${trans.collectionDate}: ${dateStr}`, `${trans.collectionTime}: ${timeStr}`, Math.round(13 * scale));
        y += Math.round(18 * scale);

        drawSeparator();
        y += Math.round(16 * scale);

        // 5. Parameters (Litres, Fat, SNF, Rate)
        const params = [
            { label: trans.litres, value: data.litres != null ? data.litres.toFixed(3) : '0.000' },
            { label: trans.fat, value: data.fat != null ? data.fat.toFixed(1) : '0.0' },
            { label: trans.snf, value: data.snf != null ? data.snf.toFixed(1) : '0.0' },
            { label: trans.rate, value: data.rate != null ? data.rate.toFixed(2) : '0.00' }
        ];

        params.forEach(param => {
            drawTextRow(param.label + ' :', param.value, Math.round(13.5 * scale), 'bold');
            y += Math.round(19 * scale);
        });

        drawSeparator();
        y += Math.round(16 * scale);

        // 6. Total Amount (bold)
        const amount = data.amount != null ? data.amount.toFixed(2) : '0.00';
        drawTextRow(trans.amount + ' :', `₹ ${amount}`, Math.round(16 * scale), 'bold');
        y += Math.round(22 * scale);

        // 7. Thank You
        drawTextCenter(trans.thankYou, Math.round(12 * scale), 'bold');
        y += Math.round(16 * scale);

        // 8. Powered by
        drawTextCenter("Dinexa SMART POS", Math.round(11 * scale), '800');
        y += Math.round(6 * scale);

        return y;
    }

    static async _renderCoupon1Template(ctx, order, trans, width, scale, y, lang, paymentMode) {
        const fontDev = `'Noto Sans Devanagari', 'Mukta', 'Devanagari MT', Arial, sans-serif`;
        const is80mm = width > 450;
        const settings = BluetoothPrinterManager.getSettings();
        const marginOffset = settings.marginOffset !== undefined ? settings.marginOffset : 0;
        const contentWidth = is80mm ? 536 : (settings.printableWidth || 300);
        const L_MARGIN = is80mm ? 16 : marginOffset;
        const R_BOUND = is80mm ? width - 20 : Math.min(360, L_MARGIN + contentWidth);
        const C_X = (L_MARGIN + R_BOUND) / 2;

        const drawTextCenter = (text, fontSizePx, fontStyle = '') => {
            ctx.font = `${fontStyle} ${fontSizePx}px ${fontDev}`;
            ctx.textAlign = 'center';
            ctx.fillText(text, C_X, y);
        };

        const drawTextRow = (leftText, rightText, fontSizePx, fontStyle = '') => {
            ctx.font = `${fontStyle} ${fontSizePx}px ${fontDev}`;
            ctx.textAlign = 'left';
            ctx.fillText(leftText, L_MARGIN, y);
            ctx.textAlign = 'right';
            ctx.fillText(rightText, R_BOUND, y);
        };

        const drawSeparator = () => {
            ctx.beginPath();
            ctx.setLineDash([4, 4]);
            ctx.moveTo(L_MARGIN, y - Math.round(6 * scale));
            ctx.lineTo(R_BOUND, y - Math.round(6 * scale));
            ctx.lineWidth = 1.5;
            ctx.stroke();
            ctx.setLineDash([]);
        };

        const currentUser = JSON.parse(sessionStorage.getItem('react_pos_current_user') || localStorage.getItem('react_pos_current_user') || '{}');
        let orderBranch = null;
        try {
            const cachedBranches = JSON.parse(localStorage.getItem('react_pos_branches') || sessionStorage.getItem('react_pos_branches') || '[]');
            if (Array.isArray(cachedBranches) && (order.branch_id || order.branchId)) {
                const bId = order.branch_id || order.branchId;
                orderBranch = cachedBranches.find(b => String(b.id) === String(bId));
            }
        } catch (e) { }

        const hotelName = order.hotelName || order.hotel_name || currentUser.tenant_name || currentUser.name || trans.companyName || 'HOTEL MARDANI';
        const branchName = order.branch_name || order.branchName || orderBranch?.name || orderBranch?.branch_name || (currentUser.branch_name && currentUser.branch_name.toLowerCase() !== 'main branch' ? currentUser.branch_name : '') || '';
        const couponNum = order.coupon_number || order.token_number || order.couponNumber || order.tokenNumber || (order.id ? order.id.replace('ORD-', '') : '1');

        // 1. TOP HEADER (Left: Hotel Name + Branch, Right: Box with Coupon No.)
        const boxWidth = Math.round((is80mm ? 90 : 58) * scale);
        const boxHeight = Math.round((is80mm ? 54 : 40) * scale);
        const boxX = R_BOUND - boxWidth;
        const boxY = y - Math.round(14 * scale);

        // Draw Right Coupon Box
        ctx.lineWidth = 1.5;
        ctx.strokeStyle = '#000000';
        ctx.strokeRect(boxX, boxY, boxWidth, boxHeight);

        // Text inside box: "Coupon No."
        ctx.font = `bold ${Math.round(10 * scale)}px ${fontDev}`;
        ctx.textAlign = 'center';
        ctx.fillText("Coupon No.", boxX + (boxWidth / 2), boxY + Math.round(13 * scale));

        // Big Coupon Number inside box
        ctx.font = `900 ${Math.round(21 * scale)}px ${fontDev}`;
        ctx.textAlign = 'center';
        ctx.fillText(String(couponNum), boxX + (boxWidth / 2), boxY + Math.round(34 * scale));

        // Draw Left Hotel Name & Branch
        const leftMaxWidth = boxX - L_MARGIN - 6;
        let leftY = y;

        ctx.font = `900 ${Math.round(15 * scale)}px ${fontDev}`;
        ctx.textAlign = 'left';
        const words = hotelName.toUpperCase().split(' ');
        let line = '';
        for (let n = 0; n < words.length; n++) {
            const testLine = line + words[n] + ' ';
            if (ctx.measureText(testLine).width > leftMaxWidth && n > 0) {
                ctx.fillText(line.trim(), L_MARGIN, leftY);
                line = words[n] + ' ';
                leftY += Math.round(19 * scale);
            } else {
                line = testLine;
            }
        }
        if (line.trim()) {
            ctx.fillText(line.trim(), L_MARGIN, leftY);
            leftY += Math.round(15 * scale);
        }

        if (branchName) {
            ctx.font = `bold ${Math.round(12 * scale)}px ${fontDev}`;
            const branchWords = branchName.split(' ');
            let bLine = '';
            for (let bn = 0; bn < branchWords.length; bn++) {
                const testBLine = bLine + branchWords[bn] + ' ';
                if (ctx.measureText(testBLine).width > leftMaxWidth && bn > 0) {
                    ctx.fillText(bLine.trim(), L_MARGIN, leftY);
                    bLine = branchWords[bn] + ' ';
                    leftY += Math.round(15 * scale);
                } else {
                    bLine = testBLine;
                }
            }
            if (bLine.trim()) {
                ctx.fillText(bLine.trim(), L_MARGIN, leftY);
                leftY += Math.round(15 * scale);
            }
        }

        y = Math.max(boxY + boxHeight + Math.round(14 * scale), leftY + Math.round(12 * scale));

        // 2. Date & Time Row
        const orderTimestamp = order.createdAt || order.timestamp || new Date().toISOString();
        const dateObj = new Date(orderTimestamp);
        const dateStr = `${dateObj.getDate()}/${dateObj.getMonth() + 1}/${dateObj.getFullYear()}`;
        const timeStr = dateObj.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        drawTextRow(`दिनांक: ${dateStr}`, timeStr, Math.round(13 * scale), 'bold');
        y += Math.round(18 * scale);

        // 3. Dashed Separator
        drawSeparator();
        y += Math.round(16 * scale);

        // 4. Columns Header: पदार्थ (Left) | Qty * Rate (Right)
        drawTextRow("पदार्थ", "Qty * Rate", Math.round(13 * scale), '900');
        y += Math.round(18 * scale);

        // 5. Items List
        (order.items || []).forEach(item => {
            const menuItem = POS_MENU.find(m => String(m.id) === String(item.id));
            let displayName = item.name;
            if (menuItem) {
                if (lang === 'mr') displayName = menuItem.nameMr || menuItem.name;
                else if (lang === 'hi') displayName = menuItem.nameHi || menuItem.name;
            }

            ctx.font = `bold ${Math.round(13.5 * scale)}px ${fontDev}`;
            ctx.textAlign = 'left';

            const maxNameWidth = is80mm ? 300 : 155;
            let nameToPrint = displayName;
            if (ctx.measureText(nameToPrint).width > maxNameWidth) {
                nameToPrint = nameToPrint.substring(0, 13) + '..';
            }
            ctx.fillText(nameToPrint, L_MARGIN, y);

            ctx.textAlign = 'right';
            ctx.fillText(`${item.quantity} x ₹${item.price}`, R_BOUND, y);
            y += Math.round(19 * scale);
        });

        // 6. Dashed Separator
        drawSeparator();
        y += Math.round(18 * scale);

        // 7. Total Amount (Large Sharp Font)
        ctx.font = `900 ${Math.round(14 * scale)}px ${fontDev}`;
        ctx.textAlign = 'left';
        ctx.fillText("एकूण बिल रक्कम", L_MARGIN, y);
        ctx.textAlign = 'right';
        ctx.font = `900 ${Math.round(18 * scale)}px ${fontDev}`;
        ctx.fillText(`${Number(order.total || 0).toFixed(0)}`, R_BOUND, y);
        y += Math.round(22 * scale);

        // 8. Dashed Separator
        drawSeparator();
        y += Math.round(16 * scale);

        // 9. Footer
        drawTextCenter("** Visit Again **", Math.round(12 * scale), 'bold');
        y += Math.round(16 * scale);
        drawTextCenter("powered by- DinexaPOS", Math.round(11 * scale), '800');

        y += Math.round(6 * scale);

        return y;
    }

    static async _renderCoupon2Template(ctx, order, trans, width, scale, y, lang, paymentMode) {
        const fontDev = `'Noto Sans Devanagari', 'Mukta', 'Devanagari MT', Arial, sans-serif`;
        const is80mm = width > 450;
        const settings = BluetoothPrinterManager.getSettings();
        const marginOffset = settings.marginOffset !== undefined ? settings.marginOffset : 0;
        const contentWidth = is80mm ? 536 : (settings.printableWidth || 300);
        const L_MARGIN = is80mm ? 16 : marginOffset;
        const R_BOUND = is80mm ? width - 20 : Math.min(360, L_MARGIN + contentWidth);
        const C_X = (L_MARGIN + R_BOUND) / 2;

        const drawTextCenter = (text, fontSizePx, fontStyle = '') => {
            ctx.font = `${fontStyle} ${fontSizePx}px ${fontDev}`;
            ctx.textAlign = 'center';
            ctx.fillText(text, C_X, y);
        };

        const drawTextRow = (leftText, rightText, fontSizePx, fontStyle = '') => {
            ctx.font = `${fontStyle} ${fontSizePx}px ${fontDev}`;
            ctx.textAlign = 'left';
            ctx.fillText(leftText, L_MARGIN, y);
            ctx.textAlign = 'right';
            ctx.fillText(rightText, R_BOUND, y);
        };

        const drawSeparator = () => {
            ctx.beginPath();
            ctx.setLineDash([4, 4]);
            ctx.moveTo(L_MARGIN, y - Math.round(6 * scale));
            ctx.lineTo(R_BOUND, y - Math.round(6 * scale));
            ctx.lineWidth = 1.5;
            ctx.stroke();
            ctx.setLineDash([]);
        };

        const currentUser = JSON.parse(sessionStorage.getItem('react_pos_current_user') || localStorage.getItem('react_pos_current_user') || '{}');
        const hotelName = order.hotelName || order.hotel_name || currentUser.tenant_name || currentUser.name || trans.companyName || 'HOTEL MARDANI';
        const branchName = order.branch_name || order.branchName || (currentUser.branch_name && currentUser.branch_name.toLowerCase() !== 'main branch' ? currentUser.branch_name : '') || '';
        const couponNum = order.coupon_number || order.token_number || order.couponNumber || order.tokenNumber || (order.id ? order.id.replace('ORD-', '') : '1');

        // 1. Centered Large Token Header Badge
        const badgeW = R_BOUND - L_MARGIN;
        const badgeH = Math.round(36 * scale);
        const badgeX = L_MARGIN;
        const badgeY = y - Math.round(14 * scale);

        ctx.fillStyle = '#ffffff';
        ctx.fillRect(badgeX, badgeY, badgeW, badgeH);

        ctx.lineWidth = 1.5;
        ctx.strokeStyle = '#000000';
        ctx.strokeRect(badgeX, badgeY, badgeW, badgeH);

        ctx.fillStyle = '#000000';
        ctx.font = `900 ${Math.round(18 * scale)}px ${fontDev}`;
        ctx.textAlign = 'center';
        ctx.fillText(`COUPON  #${couponNum}`, C_X, y + Math.round(10 * scale));

        y += Math.round(34 * scale);

        // 2. Hotel Name & Branch
        drawTextCenter(hotelName.toUpperCase(), Math.round(15 * scale), '900');
        y += Math.round(18 * scale);
        if (branchName) {
            drawTextCenter(`Branch: ${branchName}`, Math.round(12 * scale), 'bold');
            y += Math.round(15 * scale);
        }

        // 3. Date & Time
        const orderTimestamp = order.createdAt || order.timestamp || new Date().toISOString();
        const dateObj = new Date(orderTimestamp);
        const dateStr = `${dateObj.getDate()}/${dateObj.getMonth() + 1}/${dateObj.getFullYear()}`;
        const timeStr = dateObj.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        drawTextRow(`दिनांक: ${dateStr}`, timeStr, Math.round(13 * scale), 'bold');
        y += Math.round(18 * scale);

        drawSeparator();
        y += Math.round(16 * scale);

        // 4. Columns: पदार्थ | नग x दर
        drawTextRow("पदार्थ", "नग x दर", Math.round(13 * scale), '900');
        y += Math.round(18 * scale);

        (order.items || []).forEach(item => {
            const menuItem = POS_MENU.find(m => String(m.id) === String(item.id));
            let displayName = item.name;
            if (menuItem) {
                if (lang === 'mr') displayName = menuItem.nameMr || menuItem.name;
                else if (lang === 'hi') displayName = menuItem.nameHi || menuItem.name;
            }

            ctx.font = `bold ${Math.round(13.5 * scale)}px ${fontDev}`;
            ctx.textAlign = 'left';
            const maxNameWidth = is80mm ? 300 : 175;
            let nameToPrint = displayName;
            if (ctx.measureText(nameToPrint).width > maxNameWidth) {
                nameToPrint = nameToPrint.substring(0, 14) + '..';
            }
            ctx.fillText(nameToPrint, L_MARGIN, y);
            ctx.textAlign = 'right';
            ctx.fillText(`${item.quantity} x ₹${item.price}`, R_BOUND, y);
            y += Math.round(19 * scale);
        });

        drawSeparator();
        y += Math.round(18 * scale);

        // 5. Total
        drawTextRow("एकूण रक्कम :", `₹${Number(order.total || 0).toFixed(0)}`, Math.round(14 * scale), '900');
        y += Math.round(22 * scale);

        drawSeparator();
        y += Math.round(16 * scale);

        // 6. Footer
        drawTextCenter("** धन्यवाद! पुन्हा भेटूया! **", Math.round(12 * scale), 'bold');
        y += Math.round(16 * scale);
        drawTextCenter("Powered by Dinexa POS", Math.round(11 * scale), '800');

        y += Math.round(6 * scale);

        return y;
    }

    static async _renderTemplate1(ctx, order, trans, width, scale, y, lang, paymentMode) {
        const is80mm = width > 450;
        const settings = BluetoothPrinterManager.getSettings();
        const marginOffset = settings.marginOffset !== undefined ? settings.marginOffset : 0;
        const contentWidth = is80mm ? 536 : (settings.printableWidth || 300);
        const L_MARGIN = is80mm ? 16 : marginOffset;
        const R_BOUND = is80mm ? width - 20 : Math.min(360, L_MARGIN + contentWidth);
        const C_X = (L_MARGIN + R_BOUND) / 2;

        const drawTextCenter = (text, fontSizePx, fontStyle = '') => {
            ctx.font = `${fontStyle} ${fontSizePx}px Arial, sans-serif`;
            ctx.textAlign = 'center';
            ctx.fillText(text, C_X, y);
        };

        const drawTextRow = (leftText, rightText, fontSizePx, fontStyle = '') => {
            ctx.font = `${fontStyle} ${fontSizePx}px Arial, sans-serif`;
            ctx.textAlign = 'left';
            ctx.fillText(leftText, L_MARGIN, y);
            ctx.textAlign = 'right';
            ctx.fillText(rightText, R_BOUND, y);
        };

        const drawTextCenterMultiline = (text, fontSizePx, fontStyle = '', lineHeightMultiplier = 1.2) => {
            ctx.font = `${fontStyle} ${fontSizePx}px Arial, sans-serif`;
            ctx.textAlign = 'center';
            const words = text.split(' ');
            let line = '';
            const lineHeight = Math.round(fontSizePx * lineHeightMultiplier);
            const maxWidth = R_BOUND - L_MARGIN;
            for (let n = 0; n < words.length; n++) {
                const testLine = line + words[n] + ' ';
                const metrics = ctx.measureText(testLine);
                if (metrics.width > maxWidth && n > 0) {
                    ctx.fillText(line.trim(), C_X, y);
                    line = words[n] + ' ';
                    y += lineHeight;
                } else {
                    line = testLine;
                }
            }
            ctx.fillText(line.trim(), C_X, y);
            y += Math.round(fontSizePx * 1.2);
        };

        const drawSeparator = () => {
            ctx.beginPath();
            ctx.setLineDash([4, 4]);
            ctx.moveTo(L_MARGIN, y - Math.round(6 * scale));
            ctx.lineTo(R_BOUND, y - Math.round(6 * scale));
            ctx.lineWidth = 1;
            ctx.stroke();
            ctx.setLineDash([]);
        };

        const currentUser = JSON.parse(sessionStorage.getItem('react_pos_current_user') || localStorage.getItem('react_pos_current_user') || '{}');
        let orderBranch = null;
        try {
            const cachedBranches = JSON.parse(localStorage.getItem('react_pos_branches') || sessionStorage.getItem('react_pos_branches') || '[]');
            if (Array.isArray(cachedBranches) && (order.branch_id || order.branchId)) {
                const bId = order.branch_id || order.branchId;
                orderBranch = cachedBranches.find(b => String(b.id) === String(bId));
            }
        } catch (e) { }

        const hotelName = order.hotelName || order.hotel_name || order.branch_name || order.branchName || orderBranch?.name || orderBranch?.branch_name || (currentUser.is_multi_branch && currentUser.branch_name && currentUser.branch_name.toLowerCase() !== 'main branch' ? currentUser.branch_name : currentUser.tenant_name) || currentUser.name || trans.companyName;

        // 1. Top Center: Hotel Name
        drawTextCenterMultiline(hotelName.toUpperCase(), Math.round(18 * scale), '900');

        // 2. Address and Contact
        const address = order.branch_address || order.address || orderBranch?.address || orderBranch?.branch_address || currentUser.branch_address || trans.address;
        const phone = order.branch_phone || order.phone || orderBranch?.phone || orderBranch?.branch_phone || currentUser.branch_phone || trans.tel;

        if (address) {
            drawTextCenter(address, Math.round(12 * scale));
            y += Math.round(16 * scale);
        }
        if (phone) {
            drawTextCenter(phone, Math.round(12 * scale));
            y += Math.round(18 * scale);
        }

        // 3. Left: Date, Right: Time
        const orderTimestamp = order.createdAt || order.timestamp || new Date().toISOString();
        const dateStr = new Date(orderTimestamp).toLocaleDateString();
        const timeStr = new Date(orderTimestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        drawTextRow(`${trans.date}: ${dateStr}`, `${timeStr}`, Math.round(13 * scale), 'bold');
        y += Math.round(18 * scale);

        // 4. Left: Bill No, Right: Table No
        drawTextRow(`Bill No: ${order.id ? order.id.replace('ORD-', '') : '1'}`, `T# ${order.tableNumber || 'Parcel'}`, Math.round(13 * scale), 'bold');
        y += Math.round(18 * scale);

        // 5. Horizontal Line
        drawSeparator();
        y += Math.round(16 * scale);

        // 6. 2 Column Header
        ctx.font = `900 ${Math.round(13 * scale)}px Arial, sans-serif`;
        ctx.textAlign = 'left';
        ctx.fillText(trans.item || "Item", L_MARGIN, y);
        ctx.textAlign = 'right';
        ctx.fillText("Qty * Rate", R_BOUND, y);
        y += Math.round(18 * scale);

        // 7. Horizontal Line
        drawSeparator();
        y += Math.round(16 * scale);

        // 8. Items
        (order.items || []).forEach(item => {
            const menuItem = POS_MENU.find(m => String(m.id) === String(item.id));
            let displayName = item.name;
            if (menuItem) {
                if (lang === 'mr') displayName = menuItem.nameMr || menuItem.name;
                else if (lang === 'hi') displayName = menuItem.nameHi || menuItem.name;
            }

            ctx.font = `bold ${Math.round(13.5 * scale)}px Arial, sans-serif`;
            ctx.textAlign = 'left';

            const maxNameWidth = is80mm ? 300 : 195;
            let nameToPrint = displayName;
            if (ctx.measureText(nameToPrint).width > maxNameWidth) {
                nameToPrint = nameToPrint.substring(0, 14) + '..';
            }
            ctx.fillText(nameToPrint, L_MARGIN, y);

            ctx.textAlign = 'right';
            ctx.fillText(`${item.quantity} x ₹${item.price}`, R_BOUND, y);
            y += Math.round(19 * scale);
        });

        // 9. Horizontal Line and Bill Summary
        drawSeparator();
        y += Math.round(16 * scale);

        drawTextRow(trans.subtotal || "Subtotal", `${order.subtotal ? order.subtotal.toFixed(0) : '0'}`, Math.round(13 * scale));
        y += Math.round(18 * scale);

        if (order.tax > 0) {
            drawTextRow(trans.gst || "GST", `${order.tax.toFixed(0)}`, Math.round(13 * scale));
            y += Math.round(18 * scale);
        }

        if (order.discount > 0) {
            drawTextRow(trans.discount || "Discount", `-${order.discount.toFixed(0)}`, Math.round(13 * scale));
            y += Math.round(18 * scale);
        }

        drawTextRow(trans.totalPaid || "TOTAL", `₹${order.total ? order.total.toFixed(0) : '0'}`, Math.round(16 * scale), 'bold');
        y += Math.round(22 * scale);

        // QR Code
        y = await this._renderUPIQR(ctx, order.total, paymentMode, width, scale, y, order);

        // 10. Visit Again
        drawTextCenter("** Visit Again **", Math.round(12 * scale), 'bold');
        y += Math.round(16 * scale);

        // 11. Powered by Dinexa
        drawTextCenter("Dinexa SMART POS", Math.round(11 * scale), '800');

        y += Math.round(6 * scale);

        return y;
    }

    static async _renderTemplate2(ctx, order, trans, width, scale, y, lang, paymentMode) {
        const is80mm = width > 450;
        const settings = BluetoothPrinterManager.getSettings();
        const marginOffset = settings.marginOffset !== undefined ? settings.marginOffset : 0;
        const contentWidth = is80mm ? 536 : (settings.printableWidth || 300);
        const L_MARGIN = is80mm ? 16 : marginOffset;
        const R_BOUND = is80mm ? width - 20 : Math.min(360, L_MARGIN + contentWidth);
        const C_X = (L_MARGIN + R_BOUND) / 2;

        const drawTextCenter = (text, fontSizePx, fontStyle = '') => {
            ctx.font = `${fontStyle} ${fontSizePx}px Arial, sans-serif`;
            ctx.textAlign = 'center';
            ctx.fillText(text, C_X, y);
        };

        const drawTextRow = (leftText, rightText, fontSizePx, fontStyle = '') => {
            ctx.font = `${fontStyle} ${fontSizePx}px Arial, sans-serif`;
            ctx.textAlign = 'left';
            ctx.fillText(leftText, L_MARGIN, y);
            ctx.textAlign = 'right';
            ctx.fillText(rightText, R_BOUND, y);
        };

        const drawTextCenterMultiline = (text, fontSizePx, fontStyle = '', lineHeightMultiplier = 1.2) => {
            ctx.font = `${fontStyle} ${fontSizePx}px Arial, sans-serif`;
            ctx.textAlign = 'center';
            const words = text.split(' ');
            let line = '';
            const lineHeight = Math.round(fontSizePx * lineHeightMultiplier);
            const maxWidth = R_BOUND - L_MARGIN;
            for (let n = 0; n < words.length; n++) {
                const testLine = line + words[n] + ' ';
                const metrics = ctx.measureText(testLine);
                if (metrics.width > maxWidth && n > 0) {
                    ctx.fillText(line.trim(), C_X, y);
                    line = words[n] + ' ';
                    y += lineHeight;
                } else {
                    line = testLine;
                }
            }
            ctx.fillText(line.trim(), C_X, y);
            y += Math.round(fontSizePx * 1.2);
        };

        const drawSeparator = () => {
            ctx.beginPath();
            ctx.setLineDash([4, 4]);
            ctx.moveTo(L_MARGIN, y - Math.round(6 * scale));
            ctx.lineTo(R_BOUND, y - Math.round(6 * scale));
            ctx.lineWidth = 1;
            ctx.stroke();
            ctx.setLineDash([]);
        };

        // 1. Bold Graphic Header
        const currentUser = JSON.parse(sessionStorage.getItem('react_pos_current_user') || localStorage.getItem('react_pos_current_user') || '{}');
        let orderBranch = null;
        try {
            const cachedBranches = JSON.parse(localStorage.getItem('react_pos_branches') || sessionStorage.getItem('react_pos_branches') || '[]');
            if (Array.isArray(cachedBranches) && (order.branch_id || order.branchId)) {
                const bId = order.branch_id || order.branchId;
                orderBranch = cachedBranches.find(b => String(b.id) === String(bId));
            }
        } catch (e) { }

        const hotelName = order.hotelName || order.hotel_name || order.branch_name || order.branchName || orderBranch?.name || orderBranch?.branch_name || (currentUser.is_multi_branch && currentUser.branch_name && currentUser.branch_name.toLowerCase() !== 'main branch' ? currentUser.branch_name : currentUser.tenant_name) || currentUser.name || trans.companyName;
        const address = order.branch_address || order.address || orderBranch?.address || orderBranch?.branch_address || currentUser.branch_address || trans.address;
        const phone = order.branch_phone || order.phone || orderBranch?.phone || orderBranch?.branch_phone || currentUser.branch_phone || trans.tel;

        drawTextCenterMultiline(hotelName.toUpperCase(), Math.round(18 * scale), '900');
        if (address) {
            drawTextCenter(address, Math.round(12 * scale));
            y += Math.round(16 * scale);
        }
        if (phone) {
            drawTextCenter(phone, Math.round(12 * scale), 'bold');
            y += Math.round(18 * scale);
        }

        drawSeparator();
        y += Math.round(16 * scale);

        // 2. Order Metadata
        const orderTimestamp = order.createdAt || order.timestamp || new Date().toISOString();
        drawTextRow(`${trans.date}: ${new Date(orderTimestamp).toLocaleDateString()}`, `${trans.time}: ${new Date(orderTimestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`, Math.round(13 * scale));
        y += Math.round(18 * scale);

        drawTextRow(`Order: ${order.id ? order.id.replace('ORD-', '') : '1'}`, `Table: ${order.tableNumber || 'Parcel'}`, Math.round(13 * scale), 'bold');
        y += Math.round(18 * scale);

        const orderType = order.type || 'Dine-In';
        drawTextRow(`${trans.type}: ${orderType}`, `${trans.payment}: ${paymentMode}`, Math.round(13 * scale));
        y += Math.round(18 * scale);

        drawSeparator();
        y += Math.round(16 * scale);

        // 3. Items Header
        ctx.font = `bold ${Math.round(13 * scale)}px Arial, sans-serif`;
        ctx.textAlign = 'left';
        ctx.fillText(trans.item || "Item", L_MARGIN, y);
        ctx.textAlign = 'right';
        ctx.fillText("Amount", R_BOUND, y);
        y += Math.round(18 * scale);

        drawSeparator();
        y += Math.round(16 * scale);

        // 4. Items List (2 lines format)
        (order.items || []).forEach(item => {
            const menuItem = POS_MENU.find(m => String(m.id) === String(item.id));
            let displayName = item.name;
            if (menuItem) {
                if (lang === 'mr') displayName = menuItem.nameMr || menuItem.name;
                else if (lang === 'hi') displayName = menuItem.nameHi || menuItem.name;
            }

            ctx.font = `bold ${Math.round(13.5 * scale)}px Arial, sans-serif`;
            ctx.textAlign = 'left';
            const maxNameWidth = is80mm ? 300 : 175;
            let nameToPrint = displayName;
            if (ctx.measureText(nameToPrint).width > maxNameWidth) {
                nameToPrint = nameToPrint.substring(0, 14) + '..';
            }
            ctx.fillText(nameToPrint, L_MARGIN, y);
            y += Math.round(18 * scale);

            ctx.font = `${Math.round(12.5 * scale)}px Arial, sans-serif`;
            ctx.textAlign = 'left';
            ctx.fillText(`   ${item.quantity} x ₹${item.price}`, L_MARGIN, y);
            ctx.textAlign = 'right';
            ctx.fillText(`₹${(item.price * item.quantity).toFixed(0)}`, R_BOUND, y);
            y += Math.round(20 * scale);
        });

        drawSeparator();
        y += Math.round(16 * scale);

        // 5. Bill Summary
        drawTextRow(trans.subtotal || "Subtotal", `₹${order.subtotal ? order.subtotal.toFixed(0) : '0'}`, Math.round(13 * scale));
        y += Math.round(18 * scale);

        if (order.tax > 0) {
            drawTextRow(trans.gst || "GST", `₹${order.tax.toFixed(0)}`, Math.round(13 * scale));
            y += Math.round(18 * scale);
        }

        if (order.discount > 0) {
            drawTextRow(trans.discount || "Discount", `-₹${order.discount.toFixed(0)}`, Math.round(13 * scale));
            y += Math.round(18 * scale);
        }

        drawTextRow(trans.totalPaid || "TOTAL", `₹${order.total ? order.total.toFixed(0) : '0'}`, Math.round(14 * scale), 'bold');
        y += Math.round(22 * scale);

        // 6. UPI QR Logic
        y = await this._renderUPIQR(ctx, order.total, paymentMode, width, scale, y, order);

        drawSeparator();
        y += Math.round(16 * scale);

        drawTextCenter(trans.thankYou || "THANK YOU! VISIT AGAIN", Math.round(12 * scale), 'bold');
        y += Math.round(16 * scale);
        drawTextCenter("Powered by Dinexa POS", Math.round(11 * scale), '800');

        y += Math.round(6 * scale);
        return y;
    }

    static async _renderUPIQR(ctx, total, paymentMode, width, scale, y, order = {}) {
        let qrAmount = total; // Always show QR code for the total amount

        const currentUser = JSON.parse(sessionStorage.getItem('react_pos_current_user') || localStorage.getItem('react_pos_current_user') || '{}');
        let orderBranch = null;
        try {
            const cachedBranches = JSON.parse(localStorage.getItem('react_pos_branches') || sessionStorage.getItem('react_pos_branches') || '[]');
            if (Array.isArray(cachedBranches) && (order.branch_id || order.branchId)) {
                const bId = order.branch_id || order.branchId;
                orderBranch = cachedBranches.find(b => String(b.id) === String(bId));
            }
        } catch (e) { }

        const upiVpa = (order.upi_id || orderBranch?.upi_id || orderBranch?.upiId || currentUser.upi_id ? String(order.upi_id || orderBranch?.upi_id || orderBranch?.upiId || currentUser.upi_id).trim() : '');
        const merchantName = order.hotelName || orderBranch?.name || currentUser.upi_merchant_name || currentUser.tenant_name || 'Dinexa Merchant';

        // Only print QR code if a valid UPI ID is configured by Developer
        if (qrAmount > 0 && upiVpa !== '') {
            const upiString = `upi://pay?pa=${encodeURIComponent(upiVpa)}&pn=${encodeURIComponent(merchantName)}&am=${qrAmount.toFixed(2)}&cu=INR`;
            const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(upiString)}`;

            const qrImg = new Image();
            qrImg.src = qrUrl;
            qrImg.crossOrigin = "anonymous";

            await new Promise((resolve) => {
                qrImg.onload = () => {
                    const qrSize = Math.round(120 * scale);
                    const qrX = (width - qrSize) / 2;
                    ctx.drawImage(qrImg, qrX, y, qrSize, qrSize);
                    y += qrSize + Math.round(12 * scale);
                    ctx.font = `bold ${Math.round(13 * scale)}px Arial, sans-serif`;
                    ctx.textAlign = 'center';
                    ctx.fillText(`SCAN TO PAY ₹${qrAmount.toFixed(0)}`, width / 2, y);
                    y += Math.round(20 * scale);
                    resolve();
                };
                qrImg.onerror = () => {
                    resolve();
                };
                setTimeout(resolve, 3000);
            });
        }
        return y;
    }

    // Multilingual Canvas printer for KOT ticket
    static async printKOTCanvas(kot, lang = 'mr', paperSize) {
        const settings = BluetoothPrinterManager.getSettings();
        const actualPaperSize = paperSize || settings.pageSize;
        const is80mm = actualPaperSize === '80mm';
        const width = is80mm ? 576 : 384;
        const marginOffset = settings.marginOffset !== undefined ? settings.marginOffset : 0;
        const contentWidth = is80mm ? 536 : (settings.printableWidth || 300);
        const L_MARGIN = is80mm ? 16 : marginOffset;
        const R_BOUND = is80mm ? width - 20 : Math.min(360, L_MARGIN + contentWidth);

        const scale = settings.fontScale || 1.0;

        // Use a temporary scratch canvas of high height to draw the items dynamically
        const tempCanvas = document.createElement('canvas');
        tempCanvas.width = width;
        tempCanvas.height = 2500; // tall enough for any KOT

        const ctx = tempCanvas.getContext('2d');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, tempCanvas.width, tempCanvas.height);
        ctx.fillStyle = '#000000';

        const trans = BluetoothPrinterManager.TRANSLATIONS[lang] || BluetoothPrinterManager.TRANSLATIONS.en;
        let y = Math.round(24 * scale);

        const drawTextCenter = (text, fontSizePx, fontStyle = '') => {
            ctx.font = `${fontStyle} ${fontSizePx}px Arial, sans-serif`;
            ctx.textAlign = 'center';
            ctx.fillText(text, width / 2, y);
        };

        const drawTextRow = (leftText, rightText, fontSizePx, fontStyle = '') => {
            ctx.font = `${fontStyle} ${fontSizePx}px Arial, sans-serif`;
            ctx.textAlign = 'left';
            ctx.fillText(leftText, L_MARGIN, y);
            ctx.textAlign = 'right';
            ctx.fillText(rightText, R_BOUND, y);
        };
        const drawSeparator = () => {
            ctx.beginPath();
            ctx.setLineDash([4, 4]);
            ctx.moveTo(L_MARGIN, y - Math.round(6 * scale));
            ctx.lineTo(R_BOUND, y - Math.round(6 * scale));
            ctx.lineWidth = 1;
            ctx.stroke();
            ctx.setLineDash([]);
        };

        drawTextCenter(trans.kotHeader, Math.round(18 * scale), 'bold');
        y += Math.round(24 * scale);

        ctx.fillStyle = '#000000';
        ctx.fillRect(L_MARGIN, y - Math.round(16 * scale), R_BOUND - L_MARGIN, Math.round(28 * scale));
        ctx.fillStyle = '#ffffff';
        ctx.font = `bold ${Math.round(16 * scale)}px Arial, sans-serif`;
        ctx.textAlign = 'center';
        ctx.fillText(`${trans.table} ${kot.tableNumber}`, width / 2, y + Math.round(4 * scale));
        ctx.fillStyle = '#000000';
        y += Math.round(26 * scale);

        drawTextRow(`${trans.kotNo}: ${kot.id}`, `${trans.time}: ${new Date(kot.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`, Math.round(13 * scale));
        y += Math.round(18 * scale);
        drawTextRow(`${trans.waiter}: ${kot.waiterName}`, "", Math.round(13 * scale));
        y += Math.round(18 * scale);

        drawSeparator();
        y += Math.round(16 * scale);

        drawTextRow(trans.item || "Item (पदार्थ)", trans.qty || "Qty", Math.round(13 * scale), 'bold');
        y += Math.round(18 * scale);
        drawSeparator();
        y += Math.round(18 * scale);

        // Fetch custom items from localStorage cache if available
        let dbMenuItems = [];
        try {
            const cachedMenu = localStorage.getItem('react_pos_menu_items');
            if (cachedMenu) dbMenuItems = JSON.parse(cachedMenu);
        } catch (e) { }

        kot.items.forEach(item => {
            const menuItem = dbMenuItems.find(m => String(m.id) === String(item.id)) || (POS_MENU ? POS_MENU.find(m => String(m.id) === String(item.id)) : null);
            let displayName = item.name;
            if (lang === 'mr') {
                displayName = item.name_mr || item.nameMr || (menuItem ? (menuItem.name_mr || menuItem.nameMr) : '') || item.name;
            } else if (lang === 'hi') {
                displayName = item.name_hi || item.nameHi || (menuItem ? (menuItem.name_hi || menuItem.nameHi) : '') || item.name;
            } else {
                displayName = (menuItem ? menuItem.name : '') || item.name || 'Item';
            }

            ctx.font = `bold ${Math.round(13.5 * scale)}px Arial, sans-serif`;
            ctx.textAlign = 'left';
            ctx.fillText(displayName, L_MARGIN, y);
            ctx.textAlign = 'right';
            ctx.fillText(`x ${item.quantity}`, R_BOUND, y);
            y += Math.round(18 * scale);
            if (item.remarks) {
                ctx.font = `italic ${Math.round(12 * scale)}px Arial, sans-serif`;
                ctx.textAlign = 'left';
                ctx.fillText(`* Instruction: ${item.remarks}`, L_MARGIN + 10, y);
                y += Math.round(16 * scale);
            }
            y += Math.round(6 * scale);
        });

        drawSeparator();
        y += Math.round(18 * scale);
        drawTextCenter(trans.poweredBy, Math.round(11 * scale), '800');
        y += Math.round(6 * scale);

        // Create the final cropped canvas to exact drawn height
        const finalHeight = y;
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = finalHeight;
        const finalCtx = canvas.getContext('2d');
        finalCtx.drawImage(tempCanvas, 0, 0, width, finalHeight, 0, 0, width, finalHeight);

        await this.printCanvas(canvas);
    }
}

// Structured HTML receipt generator for high-definition, crisp System Print
export const getReceiptHTML = (order, template = 'coupon1', lang = 'mr', pageSize = '58mm', fontSize = 14) => {
    const is80 = pageSize === '80mm';
    const sizeStr = is80 ? '70mm' : '46mm';
    const pageStr = is80 ? '72mm auto' : '46mm auto';

    const currentUser = JSON.parse(sessionStorage.getItem('react_pos_current_user') || localStorage.getItem('react_pos_current_user') || '{}');
    let orderBranch = null;
    try {
        const cachedBranches = JSON.parse(localStorage.getItem('react_pos_branches') || sessionStorage.getItem('react_pos_branches') || '[]');
        if (Array.isArray(cachedBranches) && (order.branch_id || order.branchId)) {
            const bId = order.branch_id || order.branchId;
            orderBranch = cachedBranches.find(b => String(b.id) === String(bId));
        }
    } catch (e) { }

    const hotelName = order.hotelName || order.hotel_name || order.branch_name || orderBranch?.name || currentUser.tenant_name || currentUser.name || 'HOTEL MARDANI';
    const branchName = order.branch_name || order.branchName || orderBranch?.name || (currentUser.branch_name && currentUser.branch_name.toLowerCase() !== 'main branch' ? currentUser.branch_name : '') || '';
    const couponNo = order.coupon_number || order.token_number || order.couponNumber || order.tokenNumber || (order.id ? order.id.replace('ORD-', '') : '1');

    const orderTimestamp = order.createdAt || order.timestamp || order.created_at || new Date().toISOString();
    const dateObj = new Date(orderTimestamp);
    const dateStr = `${dateObj.getDate()}/${dateObj.getMonth() + 1}/${dateObj.getFullYear()}`;
    const timeStr = dateObj.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

    const rawFeed = localStorage.getItem('dinexa_print_feed_lines');
    let feedLines = 3;
    try {
        if (rawFeed !== null && !isNaN(parseInt(rawFeed, 10))) feedLines = parseInt(rawFeed, 10);
    } catch (e) { }

    const items = (order.items || []).map(item => {
        const menuItem = POS_MENU ? POS_MENU.find(m => String(m.id) === String(item.id)) : null;
        let displayName = item.name;
        if (menuItem) {
            if (lang === 'mr') displayName = menuItem.nameMr || menuItem.name;
            else if (lang === 'hi') displayName = menuItem.nameHi || menuItem.name;
        }
        return {
            name: displayName,
            quantity: item.quantity,
            price: Number(item.price || 0),
            total: Number(item.total || (item.price * item.quantity) || 0),
            qtyRate: `${item.quantity} x ₹${Number(item.price || 0)}`
        };
    });

    const totalAmt = Number(order.total || 0).toFixed(0);
    const subtotal = Number(order.subtotal || order.total || 0).toFixed(0);
    const tax = Number(order.tax || 0).toFixed(0);
    const discount = Number(order.discount || 0).toFixed(0);

    const isMr = lang === 'mr';
    const isHi = lang === 'hi';

    if (template === 'coupon1') {
        return `
            <html>
              <head>
                <style>
                  @page { size: ${pageStr}; margin: 0 !important; }
                  html, body {
                    font-family: 'Noto Sans Devanagari', 'Courier New', monospace;
                    width: ${sizeStr};
                    max-width: ${sizeStr};
                    box-sizing: border-box;
                    margin: 0 !important;
                    padding: 0 !important;
                    font-size: ${fontSize}px;
                    line-height: 1.15;
                    color: #000;
                    overflow: hidden;
                    height: auto !important;
                    min-height: 0 !important;
                    -webkit-print-color-adjust: exact;
                    print-color-adjust: exact;
                  }
                  .header-row { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 3px; width: 100%; }
                  .hotel-box { flex: 1; text-align: left; max-width: calc(100% - 58px); word-break: break-word; }
                  .hotel-name { font-size: 1.05em; font-weight: 900; line-height: 1.1; }
                  .branch-name { font-size: 0.75em; font-weight: bold; margin-top: 1px; }
                  .coupon-box {
                    border: 1.5px solid #000;
                    padding: 2px 3px;
                    text-align: center;
                    min-width: 48px;
                    max-width: 54px;
                    box-sizing: border-box;
                  }
                  .coupon-label { font-size: 0.7em; font-weight: bold; }
                  .coupon-number { font-size: 1.4em; font-weight: 900; line-height: 1; }
                  .datetime-row { display: flex; justify-content: space-between; font-size: 0.8em; font-weight: bold; margin-bottom: 2px; }
                  .dashed { border-bottom: 1.2px dashed #000; margin: 3px 0; }
                  .item-header { display: flex; justify-content: space-between; font-weight: 900; font-size: 0.85em; }
                  .item-row { display: flex; justify-content: space-between; font-weight: bold; font-size: 0.85em; padding: 1.5px 0; }
                  .total-row { display: flex; justify-content: space-between; font-weight: 900; font-size: 1.1em; padding: 1.5px 0; }
                  .center { text-align: center; }
                  .footer-top { font-weight: bold; font-size: 0.8em; margin-top: 3px; margin-bottom: 0px; }
                  .footer-bottom { font-weight: 900; font-size: 0.75em; margin-top: 1px; margin-bottom: 0px !important; padding-bottom: 0px !important; line-height: 1; }
                </style>
              </head>
              <body>
                <div class="header-row">
                  <div class="hotel-box">
                    <div class="hotel-name">${hotelName}</div>
                    ${branchName ? `<div class="branch-name">${branchName}</div>` : ''}
                  </div>
                  <div class="coupon-box">
                    <div class="coupon-label">Coupon No.</div>
                    <div class="coupon-number">${couponNo}</div>
                  </div>
                </div>
                <div class="datetime-row">
                  <span>${isMr ? 'दिनांक' : (isHi ? 'दिनांक' : 'Date')}: ${dateStr}</span>
                  <span>${timeStr}</span>
                </div>
                <div class="dashed"></div>
                <div class="item-header">
                  <span>${isMr ? 'पदार्थ' : (isHi ? 'सामग्री' : 'Item')}</span>
                  <span>Qty * Rate</span>
                </div>
                ${items.map(item => `
                  <div class="item-row">
                    <span>${item.name}</span>
                    <span>${item.qtyRate}</span>
                  </div>
                `).join('')}
                <div class="dashed"></div>
                <div class="total-row">
                  <span>${isMr ? 'एकूण बिल रक्कम' : (isHi ? 'कुल राशि' : 'TOTAL')}</span>
                  <span>${totalAmt}</span>
                </div>
                <div class="dashed"></div>
                <div class="center footer-top">** Visit Again **</div>
                <div class="center footer-bottom">powered by- DinexaPOS</div>
                ${feedLines > 0 ? `<div style="height: ${feedLines * 12}px; width: 100%; margin: 0; padding: 0;"></div>` : ''}
              </body>
            </html>
        `;
    } else if (template === 'coupon2') {
        return `
            <html>
              <head>
                <style>
                  @page { size: ${pageStr}; margin: 0 !important; }
                  html, body {
                    font-family: 'Noto Sans Devanagari', 'Courier New', monospace;
                    width: ${sizeStr};
                    max-width: ${sizeStr};
                    box-sizing: border-box;
                    margin: 0 !important;
                    padding: 0 !important;
                    font-size: ${fontSize}px;
                    line-height: 1.15;
                    color: #000;
                    overflow: hidden;
                    height: auto !important;
                    min-height: 0 !important;
                    -webkit-print-color-adjust: exact;
                    print-color-adjust: exact;
                  }
                  .center { text-align: center; }
                  .token-badge { background: #ffffff; color: #000000; border: 1.5px solid #000000; padding: 3px 6px; font-weight: 900; font-size: 1.15em; text-align: center; border-radius: 4px; margin-bottom: 3px; }
                  .hotel-name { font-size: 1.05em; font-weight: 900; }
                  .branch-name { font-size: 0.75em; font-weight: bold; margin-bottom: 2px; }
                  .datetime-row { display: flex; justify-content: space-between; font-size: 0.8em; font-weight: bold; margin-bottom: 2px; }
                  .dashed { border-bottom: 1.2px dashed #000; margin: 3px 0; }
                  .item-header { display: flex; justify-content: space-between; font-weight: 900; font-size: 0.85em; }
                  .item-row { display: flex; justify-content: space-between; font-weight: bold; font-size: 0.85em; padding: 1.5px 0; }
                  .total-row { display: flex; justify-content: space-between; font-weight: 900; font-size: 1.1em; padding: 1.5px 0; }
                  .footer-top { font-weight: bold; font-size: 0.8em; margin-top: 3px; margin-bottom: 0px; }
                  .footer-bottom { font-weight: 800; font-size: 0.75em; margin-top: 1px; margin-bottom: 0px !important; padding-bottom: 0px !important; line-height: 1; }
                </style>
              </head>
              <body>
                <div class="token-badge">COUPON #${couponNo}</div>
                <div class="center hotel-name">${hotelName}</div>
                ${branchName ? `<div class="center branch-name">Branch: ${branchName}</div>` : ''}
                <div class="datetime-row">
                  <span>${isMr ? 'दिनांक' : 'Date'}: ${dateStr}</span>
                  <span>${timeStr}</span>
                </div>
                <div class="dashed"></div>
                <div class="item-header">
                  <span>${isMr ? 'पदार्थ' : 'Item'}</span>
                  <span>${isMr ? 'नग x दर' : 'Qty x Price'}</span>
                </div>
                ${items.map(item => `
                  <div class="item-row">
                    <span>${item.name}</span>
                    <span>${item.quantity} x ₹${item.price}</span>
                  </div>
                `).join('')}
                <div class="dashed"></div>
                <div class="total-row">
                  <span>${isMr ? 'एकूण रक्कम :' : 'TOTAL :'}</span>
                  <span>₹${totalAmt}</span>
                </div>
                <div class="dashed"></div>
                <div class="center footer-top">${isMr ? '** धन्यवाद! पुन्हा भेटूया! **' : '** Thank You! Visit Again! **'}</div>
                <div class="center footer-bottom">Powered by Dinexa POS</div>
                ${feedLines > 0 ? `<div style="height: ${feedLines * 12}px; width: 100%; margin: 0; padding: 0;"></div>` : ''}
              </body>
            </html>
        `;
    } else {
        // Counter / Format 1 template
        return `
            <html>
              <head>
                <style>
                  @page { size: ${pageStr}; margin: 0 !important; }
                  html, body { 
                    font-family: 'Courier New', Courier, monospace; 
                    width: ${sizeStr}; 
                    max-width: ${sizeStr};
                    box-sizing: border-box;
                    margin: 0 !important;
                    padding: 0 !important; 
                    font-size: ${fontSize}px;
                    line-height: 1.2;
                    color: #000;
                    overflow: hidden;
                    height: auto !important;
                    min-height: 0 !important;
                    -webkit-print-color-adjust: exact;
                    print-color-adjust: exact;
                  }
                  .center { text-align: center; }
                  .bold { font-weight: bold; }
                  .header { font-size: 1.15em; margin-bottom: 2px; }
                  .subheader { font-size: 0.85em; margin-bottom: 3px; }
                  .indent-row { display: flex; margin-bottom: 2px; justify-content: space-between; font-size: 0.85em; }
                  .item-header { display: flex; justify-content: space-between; font-weight: bold; border-bottom: 1px dashed #000; margin-top: 4px; padding-bottom: 2px; font-size: 0.85em; }
                  .item-row { display: flex; justify-content: space-between; padding: 1.5px 0; font-size: 0.85em; }
                  .totals-box { margin-top: 4px; border-top: 1px dashed #000; padding-top: 2px; }
                  .total-row { display: flex; justify-content: space-between; padding: 1.5px 0; font-size: 0.85em; }
                  .final-total { font-weight: bold; font-size: 1.05em; border-top: 1px solid #000; padding-top: 2px; margin-top: 2px; }
                  .footer-text { margin-top: 4px; margin-bottom: 0px !important; padding-bottom: 0px !important; }
                </style>
              </head>
              <body>
                <div class="center bold header">${hotelName}</div>
                ${branchName ? `<div class="center subheader">${branchName}</div>` : ''}
                <div style="border-bottom: 1px dashed #000; margin-bottom: 4px;"></div>
                <div class="indent-row">
                  <span>${isMr ? 'दिनांक' : 'Date'}: ${dateStr}</span>
                  <span>${timeStr}</span>
                </div>
                <div class="indent-row bold">
                  <span>${isMr ? 'टोकन' : 'Token'}: #${couponNo}</span>
                  <span>${order.tableNumber ? `Table: ${order.tableNumber}` : ''}</span>
                </div>
                <div class="indent-row">
                  <span>Type: ${order.type || 'Counter'}</span>
                  <span>Payment: ${order.paymentMethod || 'Cash'}</span>
                </div>
                <div class="item-header">
                  <span style="flex-grow: 1;">Item</span>
                  <span style="width: 35px; text-align: center;">Qty</span>
                  <span style="width: 55px; text-align: right;">Amt</span>
                </div>
                ${items.map(item => `
                  <div class="item-row">
                    <span style="flex-grow: 1;">${item.name}</span>
                    <span style="width: 35px; text-align: center;">${item.quantity}</span>
                    <span style="width: 55px; text-align: right;">₹${item.total.toFixed(0)}</span>
                  </div>
                `).join('')}
                <div class="totals-box">
                  <div class="total-row">
                    <span>${isMr ? 'एकूण मुळ किंमत' : 'Subtotal'}</span>
                    <span>₹${subtotal}</span>
                  </div>
                  ${tax > 0 ? `
                    <div class="total-row">
                      <span>GST</span>
                      <span>₹${tax}</span>
                    </div>
                  ` : ''}
                  ${discount > 0 ? `
                    <div class="total-row" style="color: red;">
                      <span>Discount</span>
                      <span>-₹${discount}</span>
                    </div>
                  ` : ''}
                  <div class="total-row final-total">
                    <span>${isMr ? 'एकूण बिल रक्कम' : 'TOTAL'}</span>
                    <span>₹${totalAmt}</span>
                  </div>
                </div>
                <div style="border-top: 1px dashed #000; margin-top: 6px; padding-top: 4px; text-align: center; font-weight: bold;">
                  ${isMr ? 'धन्यवाद! पुन्हा भेटूया!' : 'THANK YOU! VISIT AGAIN'}
                </div>
                <div class="center footer-text" style="font-size: 0.75em; color: #666; margin-top: 2px;">Powered by Dinexa POS</div>
                ${feedLines > 0 ? `<div style="height: ${feedLines * 12}px; width: 100%; margin: 0; padding: 0;"></div>` : ''}
              </body>
            </html>
        `;
    }
};

// High-Definition System Printer for Order Receipts (used across POS, Quick POS, Tables, Reports)
export const printOrderReceipt = (order, lang = null, paperSize = null) => {
    if (!order) return false;
    const settings = BluetoothPrinterManager.getSettings();
    const actualSize = paperSize || settings.pageSize || '58mm';
    const template = settings.receiptTemplate || 'coupon1';
    const effectiveLang = lang || localStorage.getItem('dinexa_printer_lang') || 'mr';

    const html = getReceiptHTML(order, template, effectiveLang, actualSize, settings.fontSize || 14);

    let iframe = document.getElementById('dinexa-print-iframe');
    if (!iframe) {
        iframe = document.createElement('iframe');
        iframe.id = 'dinexa-print-iframe';
        iframe.style.position = 'absolute';
        iframe.style.width = '0px';
        iframe.style.height = '0px';
        iframe.style.border = 'none';
        iframe.style.left = '-9999px';
        document.body.appendChild(iframe);
    }

    const doc = iframe.contentWindow.document;
    doc.open();
    doc.write(html);
    doc.close();

    setTimeout(() => {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
    }, 250);
    return true;
};

export const getKOTHTML = (kot, lang = 'mr', pageSize = '58mm', fontSize = 14) => {
    const is80 = pageSize === '80mm';
    const sizeStr = is80 ? '70mm' : '46mm';
    const pageStr = is80 ? '72mm auto' : '46mm auto';
    const trans = BluetoothPrinterManager.TRANSLATIONS[lang] || BluetoothPrinterManager.TRANSLATIONS.en;

    const rawFeed = localStorage.getItem('dinexa_print_feed_lines');
    let feedLines = 3;
    try {
        if (rawFeed !== null && !isNaN(parseInt(rawFeed, 10))) feedLines = parseInt(rawFeed, 10);
    } catch (e) { }

    return `
        <html>
          <head>
            <style>
              @page { size: ${pageStr}; margin: 0 !important; }
              body { 
                font-family: 'Courier New', Courier, monospace; 
                width: ${sizeStr}; 
                max-width: ${sizeStr};
                box-sizing: border-box;
                margin: 0 !important;
                padding: 0 !important; 
                font-size: ${fontSize}px;
                line-height: 1.3;
                color: #000;
                overflow: hidden;
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
              }
              .center { text-align: center; }
              .bold { font-weight: bold; }
              .header { font-size: 1.2em; margin-bottom: 4px; color: #ef4444; border-bottom: 1.5px dashed #ef4444; padding-bottom: 3px; }
              .table-block { font-size: 1.25em; background-color: #ef4444; color: #fff; padding: 3px; text-align: center; margin: 4px 0; font-weight: bold; }
              .indent-row { display: flex; margin-bottom: 3px; justify-content: space-between; }
              .item-header { display: flex; justify-content: space-between; font-weight: bold; border-bottom: 1px dashed #000; margin-top: 6px; padding-bottom: 3px; }
              .item-row { padding: 3px 0; border-bottom: 1px dashed #ccc; }
              .item-main { display: flex; justify-content: space-between; font-weight: bold; }
              .remarks { font-size: 0.85em; color: #ef4444; font-style: italic; padding-left: 6px; }
            </style>
          </head>
          <body>
            <div class="center bold header">${trans.kotHeader || 'किचन ऑर्डर तिकीट (KOT)'}</div>
            <div class="table-block">${trans.table || 'टेबल'} ${kot.tableNumber || kot.table_number || ''}</div>
            <div class="indent-row">
              <span>${trans.kotNo || 'KOT नं'}: ${kot.id || '1'}</span>
              <span>${new Date(kot.timestamp || new Date()).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
            </div>
            <div class="indent-row">
              <span>${trans.waiter || 'वेटर्स'}: ${kot.waiterName || kot.waiter_name || 'Staff'}</span>
            </div>
            <div class="item-header">
              <span>${trans.item || 'पदार्थ'}</span>
              <span style="text-align: right;">${trans.qty || 'नग'}</span>
            </div>
            ${(kot.items || []).map(item => `
              <div class="item-row">
                <div class="item-main">
                  <span>${item.name}</span>
                  <span>x ${item.quantity}</span>
                </div>
                ${item.remarks ? `<div class="remarks">* Instruction: ${item.remarks}</div>` : ''}
              </div>
            `).join('')}
            <div style="margin-top: 10px; text-align: center; font-size: 0.8em; color: #666;">
              ${trans.poweredBy || 'Dinexa Smart POS Kitchen Hub'}
            </div>
            ${feedLines > 0 ? `<div style="height: ${feedLines * 12}px; width: 100%; margin: 0; padding: 0;"></div>` : ''}
          </body>
        </html>
    `;
};

export const printKOTHTML = (kot, lang = 'mr', paperSize = null) => {
    const settings = BluetoothPrinterManager.getSettings();
    const actualSize = paperSize || settings.pageSize || '58mm';
    const effectiveLang = lang || localStorage.getItem('dinexa_printer_lang') || 'mr';

    const html = getKOTHTML(kot, effectiveLang, actualSize, settings.fontSize || 14);

    let iframe = document.getElementById('dinexa-print-iframe');
    if (!iframe) {
        iframe = document.createElement('iframe');
        iframe.id = 'dinexa-print-iframe';
        iframe.style.position = 'absolute';
        iframe.style.width = '0px';
        iframe.style.height = '0px';
        iframe.style.border = 'none';
        iframe.style.left = '-9999px';
        document.body.appendChild(iframe);
    }

    const doc = iframe.contentWindow.document;
    doc.open();
    doc.write(html);
    doc.close();

    setTimeout(() => {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
    }, 250);
    return true;
};

// Helper for System Print Preview (Used by ReportsView and others)
export const printThermalReceipt = async (orderRecord, hotelName) => {
    try {
        const lang = localStorage.getItem('dinexa_receipt_lang') || 'mr';
        let bName = hotelName || orderRecord.hotelName || orderRecord.branch_name;
        let bAddress = orderRecord.branch_address;
        let bPhone = orderRecord.branch_phone;
        let bUpi = orderRecord.upi_id;

        if (orderRecord.branch_id) {
            try {
                const cachedBranches = JSON.parse(localStorage.getItem('react_pos_branches') || sessionStorage.getItem('react_pos_branches') || '[]');
                const found = cachedBranches.find(b => String(b.id) === String(orderRecord.branch_id));
                if (found) {
                    if (!bName) bName = found.name || found.branch_name;
                    if (!bAddress) bAddress = found.address || found.branch_address;
                    if (!bPhone) bPhone = found.phone || found.branch_phone;
                    if (!bUpi) bUpi = found.upi_id || found.upiId;
                }
            } catch (e) { }
        }

        const finalOrder = {
            ...orderRecord,
            hotelName: bName || orderRecord.hotelName || orderRecord.branch_name,
            branch_name: bName || orderRecord.hotelName || orderRecord.branch_name,
            branch_address: bAddress || orderRecord.branch_address,
            branch_phone: bPhone || orderRecord.branch_phone,
            upi_id: bUpi || orderRecord.upi_id
        };

        printOrderReceipt(finalOrder, lang);
    } catch (error) {
        console.error("Error printing receipt:", error);
        alert("Failed to print receipt: " + error.message);
    }
};
